import { useEffect, useRef, useState } from 'react'

export interface RowAction {
  label: string
  onSelect: () => void
}

/**
 * 행 하나가 할 수 있는 일을 전부 담는 메뉴.
 *
 * 화면 기준(`position: fixed`)으로 붙인다. 표가 `overflow` 가 걸린 칸 안에 있어서,
 * 행에 붙여두면 메뉴가 잘리거나 가로 스크롤바를 만든다. 대신 스크롤을 따라오지 못하므로
 * 스크롤이 시작되면 닫는다.
 */
export default function RowMenu({ label, actions }: { label: string; actions: RowAction[] }) {
  const [anchor, setAnchor] = useState<Anchor | null>(null)
  const button = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!anchor) return

    const close = () => setAnchor(null)
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      // 버튼 자신은 놔둔다. 여기서 닫아버리면 이어지는 click 이 도로 열어, 토글이 안 먹는다.
      if (menu.current?.contains(target) || button.current?.contains(target)) return
      close()
    }

    document.addEventListener('pointerdown', onPointerDown)
    // 캡처로 듣는다. 스크롤은 버블링하지 않는다.
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [anchor])

  useEffect(() => {
    if (anchor) items(menu.current)[0]?.focus()
  }, [anchor])

  function toggle() {
    if (anchor) {
      setAnchor(null)
      return
    }
    const rect = button.current?.getBoundingClientRect()
    if (rect) setAnchor(place(rect, actions.length))
  }

  function close() {
    setAnchor(null)
    button.current?.focus()
  }

  return (
    <>
      <button
        type="button"
        ref={button}
        className="row-menu-button"
        aria-label={`${label} 메뉴`}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        onClick={toggle}
      >
        ⋯
      </button>

      {anchor && (
        <div
          ref={menu}
          className="row-menu"
          role="menu"
          style={{ right: anchor.right, top: anchor.top, bottom: anchor.bottom }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              close()
              return
            }
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
            event.preventDefault()
            const all = items(menu.current)
            const at = all.indexOf(document.activeElement as HTMLButtonElement)
            const step = event.key === 'ArrowDown' ? 1 : -1
            all[(at + step + all.length) % all.length]?.focus()
          }}
        >
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setAnchor(null)
                action.onSelect()
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </>
  )
}

/** 열린 메뉴가 놓일 자리, 화면 기준. */
interface Anchor {
  right: number
  top?: number
  bottom?: number
}

const ITEM_HEIGHT = 34
const MENU_PADDING = 12
const GAP = 4

/** 아래로 나가면 위로 편다. 목록 맨 아랫줄에서도 메뉴가 화면 밖으로 나가지 않게. */
function place(button: DOMRect, count: number): Anchor {
  const right = window.innerWidth - button.right
  const height = count * ITEM_HEIGHT + MENU_PADDING
  return button.bottom + height + GAP > window.innerHeight
    ? { right, bottom: window.innerHeight - button.top + GAP }
    : { right, top: button.bottom + GAP }
}

function items(container: HTMLDivElement | null): HTMLButtonElement[] {
  return [...(container?.querySelectorAll('button') ?? [])]
}
